import React, {
  Dispatch,
  SetStateAction,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react'
import type { TourProps } from 'antd'
import {
  Table,
  DatePicker,
  Select,
  Button,
  Tooltip,
  Alert
} from 'antd'
import { ClearOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import axios from './utils/accountApi'
import dayjs, { Dayjs } from 'dayjs'
import LoadingCard from './components/LoadingCard'
import AppPageHeader from './components/AppPageHeader'
import { resolveFrontendApiBase } from './utils/apiBase'
import AppTour, { renderAppTourPanel } from './tours/AppTour'
import { tourTarget, useGuidedTour } from './tours/useGuidedTour'
import { getAllLanguages } from './LanguageConfig'
import { getTargetLanguageFromPair, splitLanguagePair } from './utils/languagePairs'
import { canonicalizeLanguageKey, canonicalizeLanguageName } from './utils/languageIdentity'
import './CreditHistory.css'

const { RangePicker } = DatePicker
const { Option } = Select
const API_BASE = resolveFrontendApiBase()

export interface UsersSharedState {
  userID: string
  accountType: number
  credits: number
  roles: string[]
  isLangTesterUser: boolean
}

interface Transaction {
  id: string
  book: string
  book_guid: string
  chapter: string
  chapter_guid: string
  credits: number
  credits_after: number
  credits_before: number
  description: string
  language: string
  timestamp: string
  type: string
  hold_id?: string
  hold_status?: string
  hold_remaining_amount?: number
  hold_release_reason?: string
  hold_created_at?: string
  hold_expires_at?: string
  hold_released_at?: string
  hold_committed_at?: string
  hold_expired_at?: string
  deducted_rollover?: number
  deducted_monthly?: number
  deducted_forever?: number
  deduction_source_unknown?: boolean
}

interface BookOption {
  guid: string
  name: string
}

interface CreditHistorySummaryGroup {
  book_guid: string
  book: string
  target_language: string
  transaction_count: number
  deducted_credits: number
  added_credits: number
  net_credits: number
}

interface CreditHistorySummary {
  total_deducted: number
  total_added: number
  net_credits: number
  groups: CreditHistorySummaryGroup[]
}

interface CreditHistoryProps {
  isAuth: boolean
  usersSharedState: UsersSharedState
  setUsersSharedState: Dispatch<SetStateAction<UsersSharedState>>
  showLoginModal: () => void
}

const EMPTY_SUMMARY: CreditHistorySummary = {
  total_deducted: 0,
  total_added: 0,
  net_credits: 0,
  groups: []
}

const KNOWN_LANGUAGE_KEYS = new Set(getAllLanguages(true).map(canonicalizeLanguageKey))

const getKnownLanguageName = (language: string): string => {
  const canonical = canonicalizeLanguageName(language)
  return KNOWN_LANGUAGE_KEYS.has(canonicalizeLanguageKey(canonical)) ? canonical : ''
}

const getTargetLanguageOption = (language: string): string => {
  const target = getTargetLanguageFromPair(language)
  const knownTarget = getKnownLanguageName(target)
  if (knownTarget) return knownTarget

  const knownDirect = getKnownLanguageName(language)
  if (knownDirect) return knownDirect

  const { sourceLang } = splitLanguagePair(language)
  return getKnownLanguageName(sourceLang)
}

const buildTargetLanguageOptions = (languages: string[]): string[] => {
  const options = new Map<string, string>()
  languages.forEach((language) => {
    const target = getTargetLanguageOption(language)
    const key = canonicalizeLanguageKey(target)
    if (key && !options.has(key)) {
      options.set(key, target)
    }
  })
  return Array.from(options.values()).sort((a, b) => a.localeCompare(b))
}

const formatCreditAmount = (value: number): string => {
  const prefix = value > 0 ? '+' : value < 0 ? '-' : ''
  return `${prefix}${Math.abs(value).toLocaleString()}`
}

const CreditHistory: React.FC<CreditHistoryProps> = ({
  isAuth,
  usersSharedState,
  setUsersSharedState,
  showLoginModal
}) => {
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [bookFilter, setBookFilter] = useState<string | undefined>()
  const [languageFilter, setLanguageFilter] = useState<string[]>([])
  const [descriptionFilter, setDescriptionFilter] = useState<string | undefined>()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [bookOptions, setBookOptions] = useState<BookOption[]>([])
  const [languageOptions, setLanguageOptions] = useState<string[]>([])
  const [descriptionOptions, setDescriptionOptions] = useState<string[]>([])
  const [summary, setSummary] = useState<CreditHistorySummary>(EMPTY_SUMMARY)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [lastSuccessfulLoadAt, setLastSuccessfulLoadAt] = useState<string | null>(null)

  // Combined fetch for data and filters
  const fetchDataAndFilters = useCallback(async (
    pageNumber: number,
    size: number,
    currentBookFilter?: string,
    currentLanguageFilter: string[] = [],
    currentDescriptionFilter?: string
  ) => {
    if (!usersSharedState.userID) return
    
    setLoading(true)
    
    try {
      const baseParams: any = {
        userID: usersSharedState.userID,
        page: pageNumber,
        page_size: size,
        include_filters: true,
        include_summary: true
      }

      // Add date params if dateRange exists
      if (dateRange) {
        baseParams.start_date = dateRange[0].toISOString()
        baseParams.end_date = dateRange[1].toISOString()
      }

      // Add filter params
      if (currentBookFilter) baseParams.book_guid = currentBookFilter
      if (currentLanguageFilter.length > 0) baseParams.target_languages = currentLanguageFilter.join(',')
      if (currentDescriptionFilter) baseParams.description = currentDescriptionFilter

      const response = await axios.get<{
        transactions: Transaction[]
        total: number
        summary?: CreditHistorySummary
        filters: {
          books: BookOption[]
          languages: string[]
          descriptions: string[]
        }
      }>(`${API_BASE}/credit-history`, { params: baseParams })

      const sanitizedTransactions = (response.data.transactions || []).filter(
        (tx) => tx.type !== 'hold_release'
      )

      setTransactions(sanitizedTransactions)
      setTotal(response.data.total)
      setBookOptions(response.data.filters.books)
      setLanguageOptions(response.data.filters.languages)
      setDescriptionOptions(response.data.filters.descriptions)
      setSummary(response.data.summary || EMPTY_SUMMARY)
      setFetchError(null)
      setLastSuccessfulLoadAt(new Date().toISOString())

      // Clear filters that are no longer valid
      const availableTargetKeys = new Set(buildTargetLanguageOptions(response.data.filters.languages).map(canonicalizeLanguageKey))
      const validLanguageFilters = currentLanguageFilter.filter((language) => availableTargetKeys.has(canonicalizeLanguageKey(language)))
      if (validLanguageFilters.length !== currentLanguageFilter.length) {
        setLanguageFilter(validLanguageFilters)
      }
      if (currentDescriptionFilter && !response.data.filters.descriptions.includes(currentDescriptionFilter)) {
        setDescriptionFilter(undefined)
      }
      if (currentBookFilter && !response.data.filters.books.find(b => b.guid === currentBookFilter)) {
        setBookFilter(undefined)
      }

    } catch (err) {
      console.error('Failed to fetch data:', err)
      const detail = axios.isAxiosError(err)
        ? err.response?.data?.detail || err.message
        : err instanceof Error
          ? err.message
          : 'Unable to refresh credit history.'
      setFetchError(String(detail || 'Unable to refresh credit history.'))
    } finally {
      setLoading(false)
    }
  }, [usersSharedState.userID, dateRange])

  // Clear all filters function
  const clearAllFilters = useCallback(() => {
    setDateRange(null)
    setBookFilter(undefined)
    setLanguageFilter([])
    setDescriptionFilter(undefined)
    setPage(1)
    
    // Fetch fresh data with no filters
    fetchDataAndFilters(1, pageSize)
  }, [pageSize, fetchDataAndFilters])

  // Initial load
  useEffect(() => {
    if (!isAuth) {
      showLoginModal()
      return
    }
    if (!usersSharedState.userID) return
    
    setPage(1)
    fetchDataAndFilters(1, pageSize)
  }, [isAuth, usersSharedState.userID])

  // Handle date range changes
  const handleDateRangeChange = useCallback((dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]])
    } else {
      setDateRange(null)
    }
    // Reset filters and page when date range changes
    setBookFilter(undefined)
    setLanguageFilter([])
    setDescriptionFilter(undefined)
    setPage(1)
  }, [])

  // Effect for date range changes
  useEffect(() => {
    if (!usersSharedState.userID) return
    if (dateRange !== null || dateRange === null) {
      fetchDataAndFilters(1, pageSize)
    }
  }, [dateRange])

  // Handle individual filter changes
  const handleBookFilterChange = useCallback((value: string | undefined) => {
    setBookFilter(value)
    setPage(1)
    fetchDataAndFilters(1, pageSize, value, languageFilter, descriptionFilter)
  }, [languageFilter, descriptionFilter, pageSize, fetchDataAndFilters])

  const handleLanguageFilterChange = useCallback((value?: string[]) => {
    const nextValue = Array.isArray(value) ? value : []
    setLanguageFilter(nextValue)
    setPage(1)
    fetchDataAndFilters(1, pageSize, bookFilter, nextValue, descriptionFilter)
  }, [bookFilter, descriptionFilter, pageSize, fetchDataAndFilters])

  const handleDescriptionFilterChange = useCallback((value: string | undefined) => {
    setDescriptionFilter(value)
    setPage(1)
    fetchDataAndFilters(1, pageSize, bookFilter, languageFilter, value)
  }, [bookFilter, languageFilter, pageSize, fetchDataAndFilters])

  // Handle pagination
  const handlePaginationChange = useCallback((newPage: number, newPageSize?: number) => {
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize)
      setPage(1)
      fetchDataAndFilters(1, newPageSize, bookFilter, languageFilter, descriptionFilter)
    } else if (newPage !== page) {
      setPage(newPage)
      fetchDataAndFilters(newPage, pageSize, bookFilter, languageFilter, descriptionFilter)
    }
  }, [page, pageSize, bookFilter, languageFilter, descriptionFilter, fetchDataAndFilters])

  const handleRetryFetch = useCallback(() => {
    fetchDataAndFilters(page, pageSize, bookFilter, languageFilter, descriptionFilter)
  }, [bookFilter, descriptionFilter, fetchDataAndFilters, languageFilter, page, pageSize])

  const toSentenceCase = (value?: string) => {
    if (!value) return ''
    const normalized = value.replace(/_/g, ' ').trim()
    return normalized.charAt(0).toUpperCase() + normalized.slice(1)
  }

  const formatIso = (iso?: string) => {
    if (!iso) return null
    return dayjs(iso).format('MMM D, YYYY h:mm A')
  }

  const deriveHoldTooltip = (tx: Transaction) => {
    const parts: string[] = []
    if (tx.hold_release_reason) {
      parts.push(`Reason: ${tx.hold_release_reason}`)
    }
    const timingInfo: string[] = []
    const released = formatIso(tx.hold_released_at)
    const committed = formatIso(tx.hold_committed_at)
    const expired = formatIso(tx.hold_expired_at)
    if (released) timingInfo.push(`Released: ${released}`)
    if (committed) timingInfo.push(`Committed: ${committed}`)
    if (expired) timingInfo.push(`Expired: ${expired}`)
    if (timingInfo.length > 0) {
      parts.push(timingInfo.join(' • '))
    }
    return parts.length > 0 ? parts.join(' | ') : undefined
  }

  const HOLD_POSITIVE_STATUSES = new Set(['released', 'expired'])

  const deriveCreditDisplay = (record: Transaction): { value: number; tone: 'success' | 'danger' | 'warning' | 'neutral' } => {
    const baseCredits = Number(record.credits ?? 0)
    if (!Number.isFinite(baseCredits)) {
      return { value: 0, tone: 'neutral' }
    }
    if (record.type === 'deduction') {
      return { value: -Math.abs(baseCredits), tone: 'danger' }
    }
    if (record.type === 'hold') {
      const status = (record.hold_status || 'held').toLowerCase()
      if (HOLD_POSITIVE_STATUSES.has(status)) {
        return { value: Math.abs(baseCredits), tone: 'success' }
      }
      return { value: -Math.abs(baseCredits), tone: 'warning' }
    }
    if (record.type === 'refund') {
      return { value: Math.abs(baseCredits), tone: 'success' }
    }
    return {
      value: baseCredits >= 0 ? Math.abs(baseCredits) : baseCredits,
      tone: baseCredits >= 0 ? 'success' : 'danger'
    }
  }

  const renderTypeTag = (record: Transaction) => {
    switch (record.type) {
      case 'deduction':
        return <span className="credit-history-type-pill is-danger">Deduction</span>
      case 'addition':
        return <span className="credit-history-type-pill is-success">Addition</span>
      case 'refund':
        return <span className="credit-history-type-pill is-success">Refund</span>
      case 'hold':
        {
          const statusLabel = toSentenceCase(record.hold_status || 'held')
          const tagBody = `Hold${statusLabel ? ` · ${statusLabel}` : ''}`
          const resolved = HOLD_POSITIVE_STATUSES.has((record.hold_status || 'held').toLowerCase())
          const tag = <span className={`credit-history-type-pill ${resolved ? 'is-success' : 'is-warning'}`}>{tagBody}</span>
          const tooltip = deriveHoldTooltip(record)
          return tooltip ? <Tooltip title={tooltip}>{tag}</Tooltip> : tag
        }
      case 'hold_release':
        return <span className="credit-history-type-pill is-info">Hold Release</span>
      default:
        return <span className="credit-history-type-pill">{toSentenceCase(record.type) || 'Other'}</span>
    }
  }

  const renderDeductionSource = (record: Transaction) => {
    if (record.type !== 'deduction') {
      return {
        label: '—',
        monthly: 0,
        forever: 0,
        rollover: 0,
        unknown: false
      }
    }

    const hasRawSourceValues =
      record.deducted_monthly !== undefined ||
      record.deducted_forever !== undefined ||
      record.deducted_rollover !== undefined

    if (record.deduction_source_unknown || !hasRawSourceValues) {
      return {
        label: 'Unknown',
        monthly: 0,
        forever: 0,
        rollover: 0,
        unknown: true
      }
    }

    const monthly = Math.max(0, Number(record.deducted_monthly ?? 0))
    const forever = Math.max(0, Number(record.deducted_forever ?? 0))
    const rollover = Math.max(0, Number(record.deducted_rollover ?? 0))

    if (monthly === 0 && forever === 0 && rollover === 0) {
      return {
        label: 'None',
        monthly: 0,
        forever: 0,
        rollover: 0,
        unknown: false
      }
    }

    const labels: string[] = []
    if (monthly > 0) labels.push('Monthly')
    if (forever > 0) labels.push('Forever')
    if (rollover > 0) labels.push('Rollover')
    return {
      label: labels.join(' + '),
      monthly,
      forever,
      rollover,
      unknown: false
    }
  }

  const renderDeductionSourceCell = (record: Transaction) => {
    const breakdown = renderDeductionSource(record)
    if (breakdown.label === '—' || breakdown.label === 'Unknown' || breakdown.label === 'None') {
      return breakdown.label
    }

    const parts: string[] = []
    if (breakdown.monthly > 0) parts.push(`M: ${breakdown.monthly.toLocaleString()}`)
    if (breakdown.forever > 0) parts.push(`F: ${breakdown.forever.toLocaleString()}`)
    if (breakdown.rollover > 0) parts.push(`R: ${breakdown.rollover.toLocaleString()}`)
    const compact = parts.join(' • ')
    const charged = Math.max(0, Number(record.credits ?? 0)).toLocaleString()

    return (
      <Tooltip
        title={
          <div>
            <div>Monthly: {breakdown.monthly.toLocaleString()}</div>
            <div>Forever: {breakdown.forever.toLocaleString()}</div>
            <div>Rollover: {breakdown.rollover.toLocaleString()}</div>
            <div>Total Charged: {charged}</div>
          </div>
        }
      >
        <div>
          <div>{breakdown.label}</div>
          {compact ? (
            <div style={{ fontSize: 12, opacity: 0.75 }}>
              {compact}
            </div>
          ) : null}
        </div>
      </Tooltip>
    )
  }

  const columns = [
    {
      title: 'Date',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (ts: string) => new Date(ts).toLocaleString()
    },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    { title: 'Book', dataIndex: 'book', key: 'book' },
    { title: 'Chapter', dataIndex: 'chapter', key: 'chapter' },
    { title: 'Language', dataIndex: 'language', key: 'language' },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (_: string, record: Transaction) => renderTypeTag(record)
    },
    {
      title: 'Deducted From',
      key: 'deducted_from',
      render: (_: string, record: Transaction) => renderDeductionSourceCell(record)
    },
    {
      title: 'Credits',
      dataIndex: 'credits',
      key: 'credits',
      render: (_: any, record: Transaction) => {
        const { value, tone } = deriveCreditDisplay(record)
        return (
          <span className={`credit-history-credit is-${tone}`}>
            {value > 0 ? '+' : ''}
            {value.toLocaleString()}
          </span>
        )
      }
    }
  ]

  const totalCredits = transactions.reduce((sum, tx) => {
    if (tx.type === 'deduction') return sum - tx.credits
    if (tx.type === 'addition' || tx.type === 'refund') return sum + tx.credits
    // Exclude holds from the total since they are reservations
    return sum
  }, 0)

  // Check if any filters are active
  const targetLanguageOptions = useMemo(() => buildTargetLanguageOptions(languageOptions), [languageOptions])
  const hasActiveFilters = Boolean(dateRange !== null || bookFilter || languageFilter.length > 0 || descriptionFilter)
  const resultLabel = loading
    ? 'Loading transactions'
    : `${total.toLocaleString()} transaction${total === 1 ? '' : 's'}`
  const staleLoadLabel = lastSuccessfulLoadAt
    ? `Showing data last loaded ${dayjs(lastSuccessfulLoadAt).format('MMM D, YYYY h:mm A')}.`
    : 'No ledger data has loaded in this session yet.'
  const filteredNetCredits = Number(summary.net_credits ?? totalCredits)
  const totalCreditsTone = filteredNetCredits >= 0 ? 'success' : 'danger'
  const creditHistoryTour = useGuidedTour({
    userId: usersSharedState.userID,
    apiBase: API_BASE,
    flag: 'creditHistoryTourSeen',
    enabled: isAuth,
    autoStart: true,
    ready: !loading,
  })
  const creditHistoryTourSteps = useMemo<TourProps['steps']>(() => ([
    {
      title: 'Review credit activity',
      description: renderAppTourPanel('This page shows additions, deductions, refunds, and credit holds across your account.'),
      target: tourTarget('credit-history-header'),
    },
    {
      title: 'Filter the ledger',
      description: renderAppTourPanel('Your complete account history appears automatically. Narrow it by date, book, target language, or description.'),
      target: tourTarget('credit-history-filters'),
    },
    {
      title: 'Read transaction status',
      description: renderAppTourPanel('Type and deduction columns distinguish charges from refunds, additions, and reserved holds.'),
      target: tourTarget('credit-history-table'),
    },
    {
      title: 'Check filtered totals',
      description: renderAppTourPanel('The total reflects the current filter set and excludes pending credit holds.'),
      target: tourTarget('credit-history-summary', 'credit-history-table'),
    },
  ]), [])

  return (
    <main className="credit-history-page-shell">
      <div className="credit-history-page-inner">
        <div data-tour-id="credit-history-header">
        <AppPageHeader
          kicker="Account"
          title="Credit Usage History"
          description="Review credit additions, deductions, refunds, and holds across your books and language workflows."
          metrics={
            <>
              <span className="credit-history-header-pill" aria-live="polite">{resultLabel}</span>
              {hasActiveFilters && <span className="credit-history-header-pill is-filtered">Filtered</span>}
            </>
          }
          actions={
            <Button icon={<QuestionCircleOutlined />} onClick={creditHistoryTour.start} data-tour-id="credit-history-tour-trigger" className="app-tour-trigger">
              Take tour
            </Button>
          }
          titleId="credit-history-title"
        />
        </div>

        <section className="credit-history-workspace" aria-labelledby="credit-history-filters-title">
          <section className="credit-history-section" data-tour-id="credit-history-filters">
            <div className="credit-history-section-header">
              <div>
                <div className="credit-history-kicker">Filters</div>
                <h2 id="credit-history-filters-title">Narrow transactions</h2>
                <p>Filter by date, book, target language, or transaction description. Target language includes matching translation-pair labels.</p>
              </div>
            <Button
              icon={<ClearOutlined />}
              onClick={clearAllFilters}
              disabled={loading || !hasActiveFilters}
              type="default"
            >
              Clear All Filters
            </Button>
            </div>

            <div className="credit-history-filter-grid">
              <label className="credit-history-filter-field">
                <span>Date range</span>
                <RangePicker
                  onChange={handleDateRangeChange}
                  value={dateRange}
                />
              </label>
              <label className="credit-history-filter-field">
                <span>Book</span>
                <Select
                  placeholder="Filter by book"
                  allowClear
                  value={bookFilter}
                  onChange={handleBookFilterChange}
                  disabled={loading}
                >
                  {bookOptions.map((opt) => (
                    <Option key={opt.guid} value={opt.guid}>
                      {opt.name}
                    </Option>
                  ))}
                </Select>
              </label>
              <label className="credit-history-filter-field">
                <span>Target language</span>
                <Select
                  mode="multiple"
                  placeholder="Filter by target language"
                  allowClear
                  value={languageFilter}
                  onChange={handleLanguageFilterChange}
                  disabled={loading}
                  maxTagCount={1}
                  maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
                  maxTagTextLength={18}
                >
                  {targetLanguageOptions.map((l) => (
                    <Option key={l} value={l}>
                      {l}
                    </Option>
                  ))}
                </Select>
              </label>
              <label className="credit-history-filter-field">
                <span>Description</span>
                <Select
                  placeholder="Filter by description"
                  allowClear
                  value={descriptionFilter}
                  onChange={handleDescriptionFilterChange}
                  disabled={loading}
                >
                  {descriptionOptions.map((d) => (
                    <Option key={d} value={d}>
                      {d}
                    </Option>
                  ))}
                </Select>
              </label>
            </div>
          </section>

          <section className="credit-history-section" aria-labelledby="credit-history-transactions-title">
            <div className="credit-history-section-header">
              <div>
                <div className="credit-history-kicker">Transactions</div>
                <h2 id="credit-history-transactions-title">Usage ledger</h2>
                <p>Holds are reservations for pending jobs and are excluded from the total below.</p>
              </div>
              <div className="credit-history-legend" aria-label="Transaction type legend">
                <span className="credit-history-type-pill is-danger">Deduction</span>
                <span className="credit-history-type-pill is-success">Addition/Refund</span>
                <Tooltip title="Holds reserve credits for pending jobs and are not charges.">
                  <span className="credit-history-type-pill is-warning">Hold</span>
                </Tooltip>
              </div>
            </div>

            {loading ? (
              <div className="credit-history-loading">
                <LoadingCard label="Loading your credit history..." />
              </div>
            ) : (
              <>
                {fetchError && (
                  <Alert
                    className="credit-history-stale-alert"
                    type="error"
                    showIcon
                    message="Credit history could not refresh"
                    description={`${staleLoadLabel} ${fetchError}`}
                    action={
                      <Button size="small" onClick={handleRetryFetch} loading={loading}>
                        Retry
                      </Button>
                    }
                  />
                )}
                <section className="credit-history-summary-panel" aria-label="Filtered credit summary">
                  <div className="credit-history-summary-overview-header">
                    <span>Overall filtered totals</span>
                    <strong>{total.toLocaleString()} transaction{total === 1 ? '' : 's'}</strong>
                  </div>
                  <div className="credit-history-summary-metrics">
                    <div className="credit-history-summary-metric">
                      <span>Filtered net</span>
                      <strong className={`credit-history-total is-${totalCreditsTone}`}>
                        {formatCreditAmount(filteredNetCredits)} credits
                      </strong>
                    </div>
                    <div className="credit-history-summary-metric">
                      <span>Deducted</span>
                      <strong className="credit-history-total is-danger">
                        {Number(summary.total_deducted || 0).toLocaleString()} credits
                      </strong>
                    </div>
                    <div className="credit-history-summary-metric">
                      <span>Added/refunded</span>
                      <strong className="credit-history-total is-success">
                        {Number(summary.total_added || 0).toLocaleString()} credits
                      </strong>
                    </div>
                  </div>
                  {summary.groups.length > 0 ? (
                    <div className="credit-history-summary-groups" aria-label="Grouped credit totals by book and target language">
                      {summary.groups.slice(0, 8).map((group) => {
                        const groupTone = group.net_credits >= 0 ? 'success' : 'danger'
                        return (
                          <article
                            className="credit-history-summary-group"
                            key={`${group.book_guid || group.book}-${group.target_language}`}
                          >
                            <div>
                              <strong>{group.book || 'Unassigned'}</strong>
                              <span>{group.target_language || 'Unspecified'} · {group.transaction_count.toLocaleString()} transaction{group.transaction_count === 1 ? '' : 's'}</span>
                            </div>
                            <div className="credit-history-summary-group-values">
                              <span>Deducted {group.deducted_credits.toLocaleString()}</span>
                              <strong className={`credit-history-total is-${groupTone}`}>
                                {formatCreditAmount(group.net_credits)}
                              </strong>
                            </div>
                          </article>
                        )
                      })}
                      {summary.groups.length > 8 && (
                        <div className="credit-history-summary-more">
                          +{summary.groups.length - 8} more group{summary.groups.length - 8 === 1 ? '' : 's'}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="credit-history-summary-empty">No grouped credit usage for the current filters.</div>
                  )}
                </section>
                <div className="credit-history-table-wrap" data-tour-id="credit-history-table">
                  <Table
                    dataSource={transactions}
                    columns={columns}
                    rowKey="id"
                    scroll={{ x: 1120 }}
                    pagination={{
                      current: page,
                      pageSize,
                      total,
                      showSizeChanger: true,
                      pageSizeOptions: ['10', '20', '50', '100'],
                      onChange: handlePaginationChange
                    }}
                  />
                </div>

                <div className="credit-history-summary-row" data-tour-id="credit-history-summary">
                  <div>
                    <div className="credit-history-kicker">Filtered total</div>
                    <strong className={`credit-history-total is-${totalCreditsTone}`}>
                      {formatCreditAmount(filteredNetCredits)} credits
                    </strong>
                  </div>
                  <span>Excludes holds and reflects all matching transactions, not just this page.</span>
                </div>
              </>
            )}
          </section>
        </section>
        <AppTour
          open={creditHistoryTour.open}
          steps={creditHistoryTourSteps}
          current={creditHistoryTour.current}
          onChange={creditHistoryTour.setCurrent}
          onClose={creditHistoryTour.close}
          onFinish={creditHistoryTour.close}
        />
      </div>
    </main>
  )
}

export default CreditHistory
